/**
 * Slider.tsx - Skinnr-themed Slider component
 * 
 * CHANGE HISTORY:
 * - 251105_1516 [pending] danielbeer: Initial creation for ORCHESTRATOR-FIRST UI layer
 * 
 * A themeable range slider component that uses Skinnr's CSS variables for consistent styling.
 * Supports labels, tooltips, and custom min/max/step values.
 */

import React from 'react';
import './Slider.css';

export interface SliderProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> {
  /** Label to display above slider */
  label?: string;
  /** Show current value */
  showValue?: boolean;
  /** Value formatter function */
  formatValue?: (value: number) => string;
  /** Tooltip on hover */
  tooltip?: string;
}

export const Slider: React.FC<SliderProps> = ({
  label,
  showValue = false,
  formatValue = (v) => v.toString(),
  tooltip,
  value,
  min = 0,
  max = 100,
  step = 1,
  className = '',
  ...props
}) => {
  const numValue = typeof value === 'string' ? parseFloat(value) : (value as number) || 0;
  const percentage = ((numValue - Number(min)) / (Number(max) - Number(min))) * 100;

  return (
    <div className={`skinnr-slider ${className}`} title={tooltip}>
      {(label || showValue) && (
        <div className="skinnr-slider__header">
          {label && <label className="skinnr-slider__label">{label}</label>}
          {showValue && (
            <span className="skinnr-slider__value">{formatValue(numValue)}</span>
          )}
        </div>
      )}
      <div className="skinnr-slider__track-container">
        <input
          type="range"
          className="skinnr-slider__input"
          value={value}
          min={min}
          max={max}
          step={step}
          style={{
            '--slider-percentage': `${percentage}%`
          } as React.CSSProperties}
          {...props}
        />
      </div>
    </div>
  );
};

export default Slider;
